/** @import { SupermarketId, SupermarketMeta, Category, CategoryFilter, SortOption, Freshness } from '../types.js' */

/** @type {Record<SupermarketId, SupermarketMeta>} */
export const SUPERMARKETS = {
  ah: {
    id: 'ah',
    label: 'Albert Heijn',
    shortLabel: 'AH',
    color: '#333333',
    colorLight: '#F0F0F0',
    url: 'https://www.ah.nl',
  },
  jumbo: {
    id: 'jumbo',
    label: 'Jumbo',
    shortLabel: 'Jumbo',
    color: '#555555',
    colorLight: '#F0F0F0',
    url: 'https://www.jumbo.com',
  },
  lidl: {
    id: 'lidl',
    label: 'Lidl',
    shortLabel: 'Lidl',
    color: '#444444',
    colorLight: '#F0F0F0',
    url: 'https://www.lidl.nl',
  },
  plus: {
    id: 'plus',
    label: 'Plus',
    shortLabel: 'Plus',
    color: '#666666',
    colorLight: '#F0F0F0',
    url: 'https://www.plus.nl',
  },
  aldi: {
    id: 'aldi',
    label: 'Aldi',
    shortLabel: 'Aldi',
    color: '#3A3A3A',
    colorLight: '#F0F0F0',
    url: 'https://www.aldi.nl',
  },
  dirk: {
    id: 'dirk',
    label: 'Dirk',
    shortLabel: 'Dirk',
    color: '#4A4A4A',
    colorLight: '#F0F0F0',
    url: 'https://www.dirk.nl',
  },
  dekamarkt: {
    id: 'dekamarkt',
    label: 'DekaMarkt',
    shortLabel: 'Deka',
    color: '#505050',
    colorLight: '#F0F0F0',
    url: 'https://www.dekamarkt.nl',
  },
  ekoplaza: {
    id: 'ekoplaza',
    label: 'Ekoplaza',
    shortLabel: 'Eko',
    color: '#5A5A5A',
    colorLight: '#F0F0F0',
    url: 'https://www.ekoplaza.nl',
  },
  odin: {
    id: 'odin',
    label: 'Odin',
    shortLabel: 'Odin',
    color: '#606060',
    colorLight: '#F0F0F0',
    url: 'https://www.odin.nl',
  },
  picnic: {
    id: 'picnic',
    label: 'Picnic',
    shortLabel: 'Picnic',
    color: '#484848',
    colorLight: '#F0F0F0',
    url: 'https://www.picnic.app/nl',
  },
}

/** @type {Record<CategoryFilter, { id: CategoryFilter, label: string, icon: string }>} */
export const CATEGORIES = {
  all: { id: 'all', label: 'Alles', icon: '🌿' },
  groente: { id: 'groente', label: 'Groente', icon: '🥦' },
  fruit: { id: 'fruit', label: 'Fruit', icon: '🍎' },
  zuivel: { id: 'zuivel', label: 'Zuivel', icon: '🧀' },
  brood: { id: 'brood', label: 'Brood & granen', icon: '🍞' },
  dranken: { id: 'dranken', label: 'Dranken', icon: '🥤' },
  snacks: { id: 'snacks', label: 'Noten & snacks', icon: '🥜' },
  baby: { id: 'baby', label: 'Baby', icon: '🍼' },
  overig: { id: 'overig', label: 'Overig', icon: '📦' },
}

/** @type {Record<SortOption, { id: SortOption, label: string }>} */
export const SORT_OPTIONS = {
  relevance: { id: 'relevance', label: 'Relevantie' },
  priceAsc: { id: 'priceAsc', label: 'Prijs (laag → hoog)' },
  priceDesc: { id: 'priceDesc', label: 'Prijs (hoog → laag)' },
  discountDesc: { id: 'discountDesc', label: 'Korting (meeste eerst)' },
  supermarket: { id: 'supermarket', label: 'Supermarkt' },
}

/**
 * Get the Dutch week number + date range string for a given date.
 */
export function getWeekLabel(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7)

  // Find Monday of current week
  const monday = new Date(date)
  const dayOfWeek = monday.getDay()
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  monday.setDate(monday.getDate() + diff)

  const sunday = new Date(monday)
  sunday.setDate(sunday.getDate() + 6)

  const fmt = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`

  return `Week ${weekNo} — ${fmt(monday)} t/m ${fmt(sunday)} ${sunday.getFullYear()}`
}

/**
 * Get the Dutch week number + date range for next week.
 */
export function getNextWeekLabel() {
  const nextMonday = new Date()
  const dayOfWeek = nextMonday.getDay()
  const diff = dayOfWeek === 0 ? 1 : 8 - dayOfWeek
  nextMonday.setDate(nextMonday.getDate() + diff)
  return getWeekLabel(nextMonday)
}

const MONTHS = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december',
]

/**
 * Compute the staleness level of data based on scrapedAt timestamp.
 *
 * @param {string} [scrapedAt] - ISO 8601 timestamp
 * @returns {Freshness} 'fresh' (<24h), 'aging' (<72h), or 'stale' (>72h)
 */
export function getFreshness(scrapedAt) {
  if (!scrapedAt) return 'stale'
  const age = Date.now() - new Date(scrapedAt).getTime()
  const hours = age / (1000 * 60 * 60)
  if (hours < 24) return 'fresh'
  if (hours < 72) return 'aging'
  return 'stale'
}

/** @type {Record<Freshness, { label: string, icon: string, color: string }>} */
export const FRESHNESS_META = {
  fresh: { label: 'Actueel', icon: '●', color: '#333333' },
  aging: { label: '1-3 dagen oud', icon: '●', color: '#888888' },
  stale: { label: 'Verouderd', icon: '●', color: '#BBBBBB' },
}

/**
 * Format a price as a Dutch euro string (e.g. "€1,99").
 *
 * @param {number|null|undefined} price
 * @returns {string}
 */
export function formatPrice(price) {
  if (price === null || price === undefined) return '—'
  return `€${price.toFixed(2).replace('.', ',')}`
}

/**
 * Format an ISO timestamp as a relative time string in Dutch.
 *
 * @param {string} [isoString] - ISO 8601 timestamp
 * @returns {string} e.g. "3 uur geleden", "Zojuist"
 */
export function formatRelativeTime(isoString) {
  if (!isoString) return 'Onbekend'
  const diff = Date.now() - new Date(isoString).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Zojuist'
  if (minutes < 60) return `${minutes} min geleden`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} uur geleden`
  const days = Math.floor(hours / 24)
  return `${days} dag${days > 1 ? 'en' : ''} geleden`
}
